import React, {useEffect, useState} from 'react';
import {Box, Typography, LinearProgress} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import dayjs from 'dayjs';

import {RootState} from 'appredux/reducers';
import {apiGetTopInputProducts} from 'constants/Api';
import {ITopInputProduct} from 'constants/interfaces';

import './TopInputProductsWidget.scss';

export default function TopInputProductsWidget(): React.ReactElement {
  /* ----- date-range from global state ----- */
  const {start, end} = useSelector((s: RootState) => s.rdwDateFilter);

  const [products, setProducts] = useState<ITopInputProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    const startDate = dayjs(start).startOf('day').toISOString();
    const endDate = dayjs(end).endOf('day').toISOString();

    apiGetTopInputProducts(undefined, startDate, endDate, 4)
      .then((res) => {
        if (alive) setProducts(res || []);
      })
      .catch((err) => {
        console.error('Failed to load top input products for widget', err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [start, end]);

  /* ----- show skeleton while waiting ----- */
  if (loading) {
    return <Skeleton variant="rect" height={160} style={{borderRadius: 8}} />;
  }

  /* ----- nothing to show ----- */
  if (products.length === 0) {
    return (
      <Box className="top-inputs-widget">
        <Typography className="widget-heading">
          TOP PERFORMING INPUTS
        </Typography>
        <Typography variant="body2" color="textSecondary">
          No paid orders found for this period
        </Typography>
      </Box>
    );
  }

  const maxUnits = Math.max(...products.map((p) => p.totalUnits));

  /* ----- render widget ----- */
  return (
    <Box className="top-inputs-widget">
      <Typography className="widget-heading">TOP PERFORMING INPUTS</Typography>

      {products.map((product) => (
        <Box key={`${product.productId}`} className="input-row">
          <Box className="row-head">
            {/* inherit --> uses CSS font-size (12 px) */}
            <Typography variant="inherit">
              {product.productName || 'Unnamed'}
            </Typography>
            <Typography variant="inherit">
              {product.totalUnits.toLocaleString()} units
            </Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={maxUnits > 0 ? (product.totalUnits / maxUnits) * 100 : 0}
          />
        </Box>
      ))}
    </Box>
  );
}
